// As páginas ANTERIORES de mensagens (passo 18, PR 1).
//
// O front nunca paginou o chat: `useChatMessages` faz UM `GET` sem `before` e o servidor entrega as 50 mais
// recentes. Para `?nota=` alcançar uma nota antiga (e para a pessoa ler as notas de meses atrás), as páginas
// anteriores entram NO MESMO cache — e o refetch da primeira página (que o WebSocket, o foco e o `staleTime`
// fazem a toda hora) não pode apagá-las: `manterAnteriores`. Puras, sem React.
//
// Como o cache é UM array só (as páginas anteriores ficam coladas antes da primeira), o que muda UMA mensagem
// — apagar, editar — precisa mudá-la ali, na hora: o refetch que a invalidação dispara só refaz a primeira
// página, e a cópia velha numa página anterior ficaria na tela. `semAMensagem` e `comMensagemEditada`.
import { trechoDaCitada } from "@/lib/chat/previaDaResposta";
import type { ChatMessage, CitadaDaResposta } from "@/types/chat";

/** O `limit` padrão do `GET /chat/rooms/{id}/messages`. Uma página com menos que isto é a última. */
export const TAMANHO_DA_PAGINA = 50;

const instante = (m: ChatMessage) => Date.parse(m.created_at);

/** Junta uma página ANTERIOR (ordem cronológica) ao que já está em cache, sem repetir id. */
export function comPaginaAnterior(
  emCache: ChatMessage[] | undefined,
  anterior: ChatMessage[]
): ChatMessage[] {
  const atuais = emCache ?? [];
  const ids = new Set(atuais.map((m) => m?.id));
  return [...anterior.filter((m) => m && !ids.has(m.id)), ...atuais];
}

/**
 * Depois de refazer a PRIMEIRA página (`frescas`, cronológica), o que sobra do cache é só o que é mais
 * antigo que ela: as páginas anteriores já carregadas. Primeira página incompleta (< 50) = a conversa
 * inteira cabe nela, não há o que manter. Bolha otimista da fila offline (`queued`) nunca volta.
 */
export function manterAnteriores(
  frescas: ChatMessage[],
  emCache: ChatMessage[] | undefined
): ChatMessage[] {
  if (!emCache || emCache.length === 0 || frescas.length < TAMANHO_DA_PAGINA) return frescas;
  const corte = instante(frescas[0]);
  if (Number.isNaN(corte)) return frescas;
  const ids = new Set(frescas.map((m) => m.id));
  const antigas = emCache.filter((m) => m && !m.queued && !ids.has(m.id) && instante(m) < corte);
  return antigas.length > 0 ? [...antigas, ...frescas] : frescas;
}

/**
 * A citada como o servidor a manda quando a original foi apagada: só o cabeçalho (`message_id`, `author_id`,
 * `author_name`, `created_at`), `apagada: true` e o `tipo` da lápide (`system_event`, o `TOMBSTONE_CONTENT` do
 * servidor), e nada do conteúdo. A tela diz "Mensagem apagada" por `apagada`, nunca pelo `tipo`.
 */
function citadaApagada(c: CitadaDaResposta): CitadaDaResposta {
  return {
    message_id: c.message_id,
    author_id: c.author_id,
    author_name: c.author_name,
    created_at: c.created_at,
    apagada: true,
    tipo: "system_event",
    trecho: null,
    arquivo: null,
    duracao_sec: null,
    miniatura_url: null,
    ligacao: null,
  };
}

/**
 * O cache sem a mensagem de `messageId`, em qualquer página. UMA função para quem apaga (`useDeleteMessage`)
 * e para o evento `message:deleted` do WebSocket (quem vê a outra pessoa apagar): as duas pontas têm de tirar
 * a mensagem do mesmo jeito. Sem cache (`undefined`), devolve `undefined` e o `setQueryData` não cria nada.
 *
 * 🔴 As RESPOSTAS a ela ficam no cache, com a prévia trocada pela da apagada (`citadaApagada`): vale para toda
 * mensagem cuja `respondendo_a` aponta para `messageId`, esteja a original no cache ou não (a resposta de uma
 * página anterior já carregada cita uma original que pode estar numa página que nunca veio). A recarga da
 * primeira página não as alcança, e a pessoa não pode ficar vendo o texto de uma mensagem que já foi apagada.
 * Quem não cita `messageId` (ou já cita a apagada) sai com a MESMA referência: nenhum objeto novo à toa.
 */
export function semAMensagem(emCache: ChatMessage[], messageId: string): ChatMessage[];
export function semAMensagem(
  emCache: ChatMessage[] | undefined,
  messageId: string
): ChatMessage[] | undefined;
export function semAMensagem(emCache: ChatMessage[] | undefined, messageId: string) {
  return emCache
    ?.filter((m) => m?.id !== messageId)
    .map((m) => {
      const citada = m?.respondendo_a;
      return citada && citada.message_id === messageId && !citada.apagada
        ? { ...m, respondendo_a: citadaApagada(citada) }
        : m;
    });
}

/**
 * O cache com a versão `editada` por cima da antiga, em qualquer página (`{ ...m, ...resto }`): o que a edição
 * muda (conteúdo, `edited_at`, hashtags…) vence, e o que ela não toca fica como estava.
 *
 * 🔴 NUNCA copia `reactions` nem `reply_count` do eco. O servidor os manda SEMPRE, e vazios (`reactions: {}` e
 * `reply_count: 0`: são os defaults de quem não hidratou esses campos, no `_msg_response` do WebSocket e no
 * `ChatMessageResponse` do PATCH); copiá-los zeraria as reações e a contagem de respostas da mensagem, e numa
 * página anterior a recarga (só da primeira) não as devolve. Editar não muda nenhum dos dois: reação e resposta
 * têm eventos próprios (`reaction:update`, `reply_count:update`).
 *
 * 🔴 Nem `lembrete` (passo 18, PR 2): o PATCH o manda `null` (não hidrata) e o WebSocket nem manda a chave;
 * quem muda a marca é o `lembrete:atualizado`.
 *
 * 🔴 Nem `guardado_de` (passo 18, PR 3): a origem da nota guardada é fixa desde que a nota nasce, e a que vem no eco
 * (WebSocket e PATCH) sai CRUA, sem `acessivel`/`apagada` — copiá-la trocaria a origem calculada do cache pela crua.
 *
 * 🔴 O `respondendo_a` do eco VEM junto (é a prévia da própria resposta, hidratada na edição: o PATCH sempre o manda,
 * `null` numa mensagem que não é resposta; o WebSocket só manda a chave quando há valor, e sem ela fica a do cache).
 * Por isso `respondendo_a` NÃO está na lista de chaves descartadas acima.
 *
 * 🔴 Editar a ORIGINAL de respostas: o `trecho` da citada nas respostas dela acompanha o texto novo (`text`, ou
 * `caption` num anexo ou áudio), pela mesma regra do servidor (`trechoDaCitada`). Só o `trecho` muda; o resto da
 * citada fica. Citada já apagada não é "desapagada", e quem não cita `editada.id` (ou já tem o mesmo trecho) sai com
 * a MESMA referência.
 *
 * Id que não está no cache não entra (a edição não cria mensagem). Não muta o eco.
 */
export function comMensagemEditada(emCache: ChatMessage[], editada: ChatMessage): ChatMessage[];
export function comMensagemEditada(
  emCache: ChatMessage[] | undefined,
  editada: ChatMessage
): ChatMessage[] | undefined;
export function comMensagemEditada(emCache: ChatMessage[] | undefined, editada: ChatMessage) {
  const resto: Partial<ChatMessage> = { ...editada };
  delete resto.reactions;
  delete resto.reply_count;
  delete resto.lembrete;
  delete resto.guardado_de;
  const conteudo = editada.content as Record<string, unknown> | null | undefined;
  const trecho = trechoDaCitada((conteudo?.text ?? conteudo?.caption) as string | null | undefined);
  return emCache?.map((m) => {
    if (m?.id === editada.id) return { ...m, ...resto };
    const citada = m?.respondendo_a;
    return citada && citada.message_id === editada.id && !citada.apagada && citada.trecho !== trecho
      ? { ...m, respondendo_a: { ...citada, trecho } }
      : m;
  });
}
